import React, {useMemo, useState} from "react";
import {CircularProgress, IconButton, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, DragDropVerticalIcon} from "@hugeicons/core-free-icons";
import MultiSelectAutocomplete from "../../../../../components/inputs/multi-select-autocomplete";
import {UI, rgba} from "../../uiTokens";
import {iconBtnDangerSx} from "../editMenuStyles";
import {SubSectionHeader} from "../components/EditMenuSection";

export const WatchdogSelection = ({project, node, onUpdate}) => {
    const watchdogs = project?.watchdogs || [];
    const selectedWatchdogs = node?.data?.watchdogs || [];
    const [isLoading, setIsLoading] = useState(false);

    const handleWatchdogChange = async (id) => {
        if (isLoading) return;
        setIsLoading(true);
        const ids = selectedWatchdogs ? [...selectedWatchdogs, id] : [id];
        try {
            await onUpdate({...node, data: {...(node?.data || {}), watchdogs: ids}});
        } catch (error) {
            console.error("Error updating node:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveWatchdog = async (watchdogId) => {
        if (isLoading) return;
        setIsLoading(true);
        const updatedIds = selectedWatchdogs.filter(id => id !== watchdogId);
        try {
            await onUpdate(node);
            await onUpdate({...node, data: {...(node?.data || {}), watchdogs: updatedIds}});
        } catch (error) {
            console.error("Error updating node:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMoveWatchdog = async (fromIndex, toIndex) => {
        if (fromIndex === toIndex || isLoading) return;
        setIsLoading(true);
        const updatedIds = [...selectedWatchdogs];
        const [movedItem] = updatedIds.splice(fromIndex, 1);
        updatedIds.splice(toIndex, 0, movedItem);
        try {
            await onUpdate({...node, data: {...(node?.data || {}), watchdogs: updatedIds}});
        } catch (error) {
            console.error("Error updating node:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const selectedWatchdogsObjects = useMemo(() => {
        return selectedWatchdogs.map(selectedId => watchdogs.find(w => w.id === selectedId)).filter(Boolean);
    }, [selectedWatchdogs, watchdogs]);

    const filterWatchdogs = useMemo(() => {
        return watchdogs?.filter(watchdog => !selectedWatchdogs.includes(watchdog.id));
    }, [selectedWatchdogs, watchdogs]);

    return (
        <>
            <SubSectionHeader
                title="Watchdogs"
                action={isLoading ? <CircularProgress size={14} sx={{color: UI.accent}}/> : null}
            />
            <MultiSelectAutocomplete
                options={filterWatchdogs || []}
                multiple={false}
                selectedValues={null}
                onChange={handleWatchdogChange}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                placeholder="Select watchdogs..."
                size="small"
                blurOnSelect
                disabled={isLoading}
            />

            {selectedWatchdogsObjects.length > 0 && (
                <Stack gap={0.75} sx={{mt: 0.5}}>
                    {selectedWatchdogsObjects.map((watchdog, index) => (
                        <Stack
                            key={watchdog.id}
                            direction="row"
                            alignItems="center"
                            gap={0.75}
                            draggable={!isLoading}
                            onDragStart={(e) => {
                                if (isLoading) {
                                    e.preventDefault();
                                    return;
                                }
                                e.dataTransfer.setData("text/plain", index.toString());
                                e.dataTransfer.effectAllowed = "move";
                            }}
                            onDragOver={(e) => {
                                if (isLoading) return;
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                            }}
                            onDrop={(e) => {
                                if (isLoading) return;
                                e.preventDefault();
                                const fromIndex = parseInt(e.dataTransfer.getData("text/plain"));
                                if (fromIndex !== index) {
                                    handleMoveWatchdog(fromIndex, index);
                                }
                            }}
                            sx={{
                                p: 1,
                                border: `1px solid ${rgba(UI.ink, 0.1)}`,
                                borderRadius: "10px",
                                backgroundColor: UI.surface,
                                cursor: isLoading ? "not-allowed" : "grab",
                                opacity: isLoading ? 0.6 : 1,
                                transition: "border-color .12s ease, background-color .12s ease",
                                "&:active": {cursor: isLoading ? "not-allowed" : "grabbing"},
                                "&:hover": {borderColor: rgba(UI.accent, 0.4), backgroundColor: rgba(UI.accent, 0.03)},
                            }}
                        >
                            <HugeiconsIcon icon={DragDropVerticalIcon} size={15} color={UI.muted}/>
                            <Typography sx={{flex: 1, fontSize: 12.5, color: UI.ink}}>{watchdog?.name}</Typography>
                            <IconButton
                                size="small"
                                sx={iconBtnDangerSx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveWatchdog(watchdog.id);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                disabled={isLoading}
                            >
                                <HugeiconsIcon icon={Cancel01Icon} size={15}/>
                            </IconButton>
                        </Stack>
                    ))}
                </Stack>
            )}
        </>
    );
};
